//frontend/scr/pages/FundingIndex.tsx
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFundingIndex, apiFundingBankConfig } from "../lib/api";

export default function FundingIndex() {
  const [loading, setLoading] = useState(true);
  const [bank, setBank] = useState<any>(null);
  const [currency, setCurrency] = useState<string>("EUR");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    (async () => {
      const res = await apiFundingIndex();
      const list = res?.accounts ?? [];
      const userCurrencies = Array.from(new Set(list.map((a: any) => a.currency)));
      const initial = searchParams.get("currency") || userCurrencies[0] || "EUR";
      setCurrency(initial);
      const cfg = await apiFundingBankConfig(initial);
      setBank(cfg);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSearchParams({ currency });
    const cfg = await apiFundingBankConfig(currency);
    setBank(cfg);
  }

  if (loading) {
    return <div className="section-box skeleton">Loading…</div>;
  }

  const userCurrencies: string[] = bank?.userCurrencies ?? [];
  const selectedCurrency: string = bank?.selectedCurrency ?? currency;
  const bankConfig = bank?.bankConfig ?? null;

  const hasConfig = !!(bankConfig?.iban || bankConfig?.bank_name || bankConfig?.bic);

  return (
    // In-flow layout with generous bottom padding so the last element (button)
    // can scroll fully above the fixed bottom menu on mobile.
    <div
      className="px-4 md:px-8 max-w-4xl mx-auto space-y-6 pb-8 md:pb-8"
      style={{
        // --tabbar-h is the height of your bottom menu; fallback 64px if not set.
        paddingBottom: "calc(env(safe-area-inset-bottom) + var(--tabbar-h, 64px) + 16px)",
      }}
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Link to="/accounts" className="btn btn-white">Manage accounts</Link>
      </div>

      {/* Currency selector */}
      <div className="card-box">
        <form onSubmit={onSubmit} className="flex flex-col md:flex-row gap-3 items-start md:items-end">
          <div className="w-full md:w-auto">
            <label className="block text-sm font-medium">Select currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="input mt-1 w-full"
            >
              {userCurrencies.length === 0 ? (
                <option value="EUR">EUR</option>
              ) : (
                userCurrencies.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))
              )}
            </select>
          </div>
          <button className="btn btn-white" type="submit">Update instructions</button>
        </form>
      </div>

      {/* Deposit instructions */}
      <div className="card-box">
        <h3 className="text-lg font-semibold mb-3">
          Bank Transfer Instructions ({selectedCurrency})
        </h3>

        {!hasConfig ? (
          <div className="alert-info">
            No bank deposit configuration found for {selectedCurrency}.
          </div>
        ) : (
          <div className="divide-y">
            <KV k="Provider" v={bankConfig.provider_name} />
            <KV k="Bank name" v={bankConfig.bank_name} />
            <KV k="Bank address" v={bankConfig.bank_address} />
            <KV k="Bank country" v={bankConfig.bank_country} />
            <KV k="IBAN" v={bankConfig.iban} mono />
            <KV k="BIC" v={bankConfig.bic} mono />
            {(bankConfig.intermediary_bank_name ||
              bankConfig.intermediary_bank_bic ||
              bankConfig.intermediary_bank_address) && (
              <div className="pt-4 mt-4">
                <h4 className="font-medium mb-2">Intermediary Bank</h4>
                <KV k="Name" v={bankConfig.intermediary_bank_name} />
                <KV k="BIC" v={bankConfig.intermediary_bank_bic} mono />
                <KV k="Address" v={bankConfig.intermediary_bank_address} />
              </div>
            )}
          </div>
        )}

        <p className="text-sm mt-4">
          Use your Funding reference shown on the next step when you create a funding request.
        </p>
      </div>

            {/* Primary action (centered and in-flow on all screens) */}
      <div className="flex justify-center">
        <Link
          to={`/funding/create?currency=${encodeURIComponent(selectedCurrency)}`}
          className={`btn w-full md:w-auto flex items-center justify-center text-center ${
            !hasConfig ? "btn-disabled opacity-50 pointer-events-none" : ""
          }`}
          aria-disabled={!hasConfig}
        >
          Continue
        </Link>
      </div>
    </div>
  );
}

function KV({ k, v, mono = false }: { k: string; v?: string | null; mono?: boolean }) {
  if (!v) return null;
  return (
    <div className="flex items-start justify-between py-1 gap-4">
      <span className="shrink-0 text-gray-600">{k}</span>
      <span className={`text-right font-medium break-words ${mono ? "font-mono" : ""}`}>{v}</span>
    </div>
  );
}
